import { _breakP } from '../common/js/lib/utils.js';

const SCROLL_LISTENER_OPTS = { passive: true };

export default class UspArea {
  constructor() {
    this.container = document.querySelector('.js-uspArea');
    if (!this.container) return;
    this.track = this.container.querySelector('.js-uspAreaSpScrollTrack');
    this.appeals = this.container.querySelectorAll('.js-uspAreaAppeal');
    this.triggers = this.container.querySelectorAll('.js-uspAreaTrigger');
    this._scrollHandler = this._onScroll.bind(this);
    this._ticking = false;
    this._rafId = null;
    this._prefersReducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    /** @type {Array<{ btn: Element, handler: () => void }>} */
    this._triggerHandlers = [];
    if (_breakP.SP) {
      this.init();
    }
  }

  init() {
    if (!this.track || this.appeals.length < 2) return;
    window.addEventListener('scroll', this._scrollHandler, SCROLL_LISTENER_OPTS);
    this._bindTriggers();
    this._updateProgress();
  }

  _bindTriggers() {
    if (this.triggers.length !== this.appeals.length) return;
    this.triggers.forEach((btn, index) => {
      const handler = () => {
        this._scrollToAppealIndex(index);
      };
      btn.addEventListener('click', handler);
      this._triggerHandlers.push({ btn, handler });
    });
  }

  _onScroll() {
    if (this._ticking) return;
    this._ticking = true;
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      this._ticking = false;
      this._updateProgress();
    });
  }

  /** トラック通過率 0〜1（_scroll-fade-bg-controller initMobile と同式） */
  _computeProgress() {
    const vh = window.innerHeight;
    const rect = this.track.getBoundingClientRect();
    const h = this.track.offsetHeight;
    if (rect.bottom <= 0) {
      return 1;
    }
    if (rect.top >= vh) {
      return 0;
    }
    const scrolled = Math.max(0, -rect.top);
    const scrollable = Math.max(0, h - vh);
    if (scrollable <= 0) {
      return 0;
    }
    return Math.min(1, Math.max(0, scrolled / scrollable));
  }

  _updateProgress() {
    this._setAppealByProgress(this._computeProgress());
  }

  /**
   * _setAppealByProgress と同じ index 割当（progress から）
   * @param {number} progress
   * @param {number} n
   */
  _indexFromProgress(progress, n) {
    if (n < 1) return 0;
    const p = Math.min(1, Math.max(0, progress));
    return n === 1 ? 0 : Math.min(n - 1, Math.max(0, Math.floor(n * p)));
  }

  /**
   * 既存の floor(n*p) 分割と整合する代表 progress
   * @param {number} index
   * @param {number} n
   */
  _progressForIndex(index, n) {
    if (n <= 1) return 0;
    return index / (n - 1);
  }

  /**
   * 目標 progress に対応する document スクロール位置
   * @param {number} targetProgress
   */
  _getScrollYForProgress(targetProgress) {
    const vh = window.innerHeight;
    const h = this.track.offsetHeight;
    const scrollable = Math.max(0, h - vh);
    const trackTopDoc =
      this.track.getBoundingClientRect().top + window.scrollY;
    const scrolled = Math.min(1, Math.max(0, targetProgress)) * scrollable;
    let targetY = trackTopDoc + scrolled;
    const maxY = Math.max(
      0,
      document.documentElement.scrollHeight - vh
    );
    return Math.min(maxY, Math.max(0, targetY));
  }

  _scrollToAppealIndex(index) {
    const n = this.appeals.length;
    if (index < 0 || index >= n) return;
    const currentIndex = this._indexFromProgress(this._computeProgress(), n);
    if (currentIndex === index) return;

    const targetProgress = this._progressForIndex(index, n);
    const targetY = this._getScrollYForProgress(targetProgress);
    const behavior = this._prefersReducedMotion ? 'auto' : 'smooth';
    window.scrollTo({ top: targetY, behavior });
  }

  /** 0〜1 の progress を N 等分。各等分に 1 枚。SCSS トラック高さ N×1 viewport と揃えること。 */
  _setAppealByProgress(progress) {
    const n = this.appeals.length;
    if (n < 1) return;
    const index = this._indexFromProgress(progress, n);
    for (let i = 0; i < n; i += 1) {
      this.appeals[i].classList.toggle('is-active', i === index);
    }
    if (this.triggers.length === n) {
      for (let i = 0; i < n; i += 1) {
        this.triggers[i].classList.toggle('is-active', i === index);
      }
    }
  }

  destroy() {
    window.removeEventListener('scroll', this._scrollHandler, SCROLL_LISTENER_OPTS);
    this._triggerHandlers.forEach(({ btn, handler }) => {
      btn.removeEventListener('click', handler);
    });
    this._triggerHandlers = [];
    if (this._rafId != null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }
}
